/**
 * The affiliate portal's intro tour: its steps, in order, and the version
 * they make up.
 *
 * Every affiliate reaches every screen a step links to (the rail hides only
 * Sub-affiliates, which no step names), so no step is dropped by role here;
 * `tabs` is still checked so a screen taken off the rail later takes its
 * link with it rather than leaving one that goes nowhere.
 *
 * Raise `AFFILIATE_TOUR_VERSION` when a step is added for a new module, and
 * give that step `since` set to the new version: the rail's tour entry is
 * then highlighted for everybody who saw an older tour, and opening it starts
 * on the new step. The tour never starts by itself again.
 *
 * Kept free of React so a test can read it without a DOM.
 */
import { kitText, type TourLayout, type TourLayoutMenuEntry, type TourStep } from '@affdice/portal-kit';

import { t, type AffiliateMessageKey } from './messages.ts';
import type { RailItem } from './rail.ts';
import { AFFILIATE_TOUR_MESSAGES, tourText, type AffiliateTourMessageKey } from './tour-messages.ts';

export const AFFILIATE_TOUR_VERSION = 1;

/** The id of the closing entry that stands for the tour itself. */
export const TOUR_MENU_ID = 'tour';

/** The child of Links the Creatives step points at. */
export const CREATIVES_CHILD_ID = 'creatives';

/**
 * What each step lights up in the miniature rail: rail tab ids, `'*'` for
 * everything, or `'tour'` for the closing tour entry. Creatives lights Links
 * and its Creatives child, so the miniature opens the sub-section.
 */
export const TOUR_FOCUS: Readonly<Record<string, readonly string[]>> = {
  welcome: ['*'],
  links: ['links'],
  creatives: ['links', CREATIVES_CHILD_ID],
  postbacks: ['postbacks'],
  reports: ['reports'],
  statements: ['statements', 'statement', 'payments'],
  done: [TOUR_MENU_ID],
};

/** The ids a step lights up; nothing is lit for a step with no entry. */
export function tourFocus(stepId: string): readonly string[] {
  return TOUR_FOCUS[stepId] ?? [];
}

/** What the portal already holds about the viewer. */
export interface TourViewer {
  /** The brand chosen in the rail's switcher, if one is. */
  readonly selectedBrand?: string | null;
  /** Every brand the affiliate works with. */
  readonly brands?: readonly (string | null | undefined)[];
  /** Each brand's own colour by name, from the brand list; null for none. */
  readonly brandColours?: Readonly<Record<string, string | null>>;
  /** The affiliate's own display name. */
  readonly affiliateName?: string | null;
  /** The portal state the rail identity shows under the name. */
}

/**
 * The brand the rail shows: the one chosen, else the only one, else the
 * product name (the rail's own mark when several are open and none chosen).
 */
export function tourBrand(v: TourViewer): TourLayout['brand'] {
  const known = (v.brands ?? []).filter((b): b is string => Boolean(b?.trim()));
  const chosen = v.selectedBrand?.trim();
  const name = chosen || (known.length === 1 ? known[0]! : '') || PRODUCT_NAME;
  // The brand's own colour where one brand is shown (owner, 2026-10-12);
  // the product mark keeps the portal's own.
  const colour = name === PRODUCT_NAME ? null : (v.brandColours?.[name] ?? null);
  return { name, colour };
}

const PRODUCT_NAME = 'KD Affiliate';

/** The identity block of the rail: the name and the portal state under it. */
export function tourUser(v: TourViewer): TourLayout['user'] {
  return { name: v.affiliateName?.trim() ?? '', role: tourText('intro.role') };
}

/**
 * The rail as the portal draws it: the same items, with a heading wherever
 * the group changes (as `NavList` does), a Creatives child under Links on the
 * step that points there, and on the closing step the tour entry.
 */
export function tourMenu(items: readonly RailItem[], stepId: string): TourLayoutMenuEntry[] {
  const menu: TourLayoutMenuEntry[] = [];
  let group: string | undefined;
  for (const [i, item] of items.entries()) {
    // A new group wherever the group changes, unlabelled when an item has
    // none, the way NavList splits the real rail (Support after Network).
    if (item.group !== group && (item.group || i > 0)) menu.push({ section: item.group ?? '' });
    group = item.group;
    menu.push({
      id: item.id,
      label: item.label,
      icon: item.icon,
      ...(item.id === 'links' && tourFocus(stepId).includes(CREATIVES_CHILD_ID)
        ? { children: [{ id: CREATIVES_CHILD_ID, label: t('links.section.creatives') }] }
        : {}),
    });
  }
  // The rail's tour entry sits at its foot on every step, as in the console.
  menu.push({ section: '' });
  menu.push({ id: TOUR_MENU_ID, label: kitText('tour.open'), icon: 'tour' });
  return menu;
}

/** Draws one step's main area; supplied by the screen file, which owns the JSX. */
export type TourLayoutContent = (stepId: string) => { title?: string; content?: TourLayout['content'] };

/** What the layouts are built from. */
export interface TourLayoutSource {
  readonly viewer: TourViewer;
  readonly rail: readonly RailItem[];
  readonly main: TourLayoutContent;
}

/** One step's layout: brand, user, the full rail, what is lit, and the controls. */
export function tourLayoutFor(stepId: string, source: TourLayoutSource): TourLayout {
  const main = source.main(stepId);
  const altKey = `intro.figure.alt.${stepId}`;
  return {
    brand: tourBrand(source.viewer),
    user: tourUser(source.viewer),
    menu: tourMenu(source.rail, stepId),
    focus: tourFocus(stepId),
    ...(main.title ? { title: main.title } : {}),
    ...(main.content ? { content: main.content } : {}),
    alt: altKey in AFFILIATE_TOUR_MESSAGES ? tourText(altKey as AffiliateTourMessageKey) : '',
  };
}

/** The rail tabs a step can open. */
export type AffiliateTourTab = 'links' | 'postbacks' | 'reports' | 'statements';

interface AffiliateTourStep {
  readonly id: string;
  readonly titleKey: { readonly tour: AffiliateTourMessageKey } | { readonly nav: AffiliateMessageKey };
  readonly bodyKey: AffiliateTourMessageKey;
  readonly tab?: AffiliateTourTab;
  readonly since?: number;
}

export const AFFILIATE_TOUR: readonly AffiliateTourStep[] = [
  { id: 'welcome', titleKey: { tour: 'intro.welcome.title' }, bodyKey: 'intro.welcome.body' },
  { id: 'links', titleKey: { tour: 'intro.links.title' }, bodyKey: 'intro.links.body', tab: 'links' },
  { id: 'creatives', titleKey: { tour: 'intro.creatives.title' }, bodyKey: 'intro.creatives.body', tab: 'links' },
  { id: 'postbacks', titleKey: { nav: 'nav.postbacks' }, bodyKey: 'intro.postbacks.body', tab: 'postbacks' },
  { id: 'reports', titleKey: { nav: 'nav.reports' }, bodyKey: 'intro.reports.body', tab: 'reports' },
  { id: 'statements', titleKey: { nav: 'nav.statements' }, bodyKey: 'intro.statements.body', tab: 'statements' },
  { id: 'done', titleKey: { tour: 'intro.done.title' }, bodyKey: 'intro.done.body' },
];

const TAB_LABELS: Readonly<Record<AffiliateTourTab, AffiliateMessageKey>> = {
  links: 'nav.links',
  postbacks: 'nav.postbacks',
  reports: 'nav.reports',
  statements: 'nav.statements',
};

/** The steps, in this tab's language, linking only tabs the rail shows. */
export function affiliateTourSteps(
  tabs: readonly string[],
  go: (tab: AffiliateTourTab) => void,
  layouts?: TourLayoutSource,
): TourStep[] {
  return AFFILIATE_TOUR.filter((s) => !s.tab || tabs.includes(s.tab)).map((s) => {
    return {
      id: s.id,
      title: 'tour' in s.titleKey ? tourText(s.titleKey.tour) : t(s.titleKey.nav),
      body: tourText(s.bodyKey),
      ...(s.since !== undefined ? { since: s.since } : {}),
      ...(layouts ? { layout: tourLayoutFor(s.id, layouts) } : {}),
      ...(s.tab
        ? { go: { label: tourText('intro.go', { screen: t(TAB_LABELS[s.tab]) }), onGo: () => go(s.tab!) } }
        : {}),
    };
  });
}
